import { CurrencyEnum, toast } from '@systemeio/ui'
import { COUPONS_API, createCoupon } from 'modules/coupons/api/coupon-api'
import { CouponErrorsInterface, CouponInterface } from 'modules/coupons/types/coupon-interface'
import { AddItemIcon } from 'modules/funnels/funnel/components/offer-settings/add-item-icon'
import AddCouponForm from 'modules/funnels/funnel/components/step-configuration-tab/coupon-selector/components/add-coupon-form'
import CreateCouponForm from 'modules/funnels/funnel/components/step-configuration-tab/coupon-selector/components/create-coupon-form'
import { couponDefaultErrors } from 'modules/funnels/funnel/components/step-configuration-tab/coupon-selector/constants/default-error'
import { useAvailableCoupons } from 'modules/funnels/funnel/components/step-configuration-tab/coupon-selector/hooks/use-available-coupons'
import {
  AddCouponDataInterface,
  CouponCreateInterface,
} from 'modules/funnels/funnel/components/step-configuration-tab/coupon-selector/types/add-coupon-data-interface'
import { CouponDefaultErrorsInterface } from 'modules/funnels/funnel/components/step-configuration-tab/coupon-selector/types/default-errors-interface'
import { OfferFormEditType } from 'modules/funnels/funnel/components/step-configuration-tab/offer-form-configuration-tab/types/offer-form-interface'
import CouponIcon from 'modules/funnels/funnel/icons/coupon-icon'
import TrashIcon from 'modules/funnels/funnel/icons/trash-icon'
import React, { useRef, useState } from 'react'
import Confirmation from 'shared/components/confirmation-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { fetchWithErrors, fetchWithFieldAndCommonErrors } from 'shared/utils/fetch-with-errors'

const defaultCreateFieldErrors: CouponErrorsInterface = {
  innerName: '',
  code: '',
  discountType: '',
  discountAmount: '',
  currency: '',
  expirationDate: '',
  limitOfUse: '',
}

interface CouponSelectorProps {
  currency: CurrencyEnum
  data: CouponInterface[]
  onSave: (data: OfferFormEditType['coupons']) => Promise<void>
}

function CouponSelector({ currency, data, onSave }: CouponSelectorProps) {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const [isAddFormOpen, setIsAddFormOpen] = useState(false)
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false)
  const [createFieldErrors, setCreateFieldErrors] =
    useState<CouponErrorsInterface>(defaultCreateFieldErrors)
  const [couponErrors, setCouponErrors] =
    useState<CouponDefaultErrorsInterface>(couponDefaultErrors)

  const [couponToDelete, setCouponToDelete] = useState<null | CouponInterface>(null)
  const [shouldFetchCoupons, setShouldFetchCoupons] = useState(false)
  const { availableCoupons } = useAvailableCoupons({
    currency,
    shouldFetch: shouldFetchCoupons,
    initialData: data,
  })
  const createdCouponId = useRef<number | undefined>()

  const updateData = useUpdateData({
    api: COUPONS_API,
    constantApi: [`${COUPONS_API}/list`],
  })

  const handleCreateCoupon = async (createCouponData: CouponCreateInterface) => {
    const apiData = {
      ...createCouponData,
      ...(createCouponData.discountType === 'fixed' ? { currency: currency } : {}),
    }
    await fetchWithErrors(async () => {
      const response = await createCoupon(apiData)
      await updateData(true, {})
      openAddForm(response.data.id)
    }, setCreateFieldErrors)
  }

  const handleAddCoupon = async (addCouponData: AddCouponDataInterface) => {
    const fullCoupon = availableCoupons?.find(coupon => coupon.id === addCouponData.couponId)
    if (fullCoupon) {
      await fetchWithFieldAndCommonErrors(async () => {
        const updatedCoupons = data ? [...data, fullCoupon] : [fullCoupon]
        await onSave(updatedCoupons.map(coupon => coupon.id))
        closeAddForm()
        toast.success(t('global.save'))
      }, setCouponErrors)
    }
  }

  const handleRemoveCoupon = async () => {
    if (couponToDelete) {
      const updatedCoupons = data?.filter(coupon => coupon.id !== couponToDelete.id) || []
      try {
        await onSave(updatedCoupons.map(coupon => coupon.id))
      } catch (error) {}
    }
  }

  const openCreateForm = () => {
    setIsAddFormOpen(false)
    setIsCreateFormOpen(true)
  }

  const openAddForm = (value?: number) => {
    setIsAddFormOpen(true)
    setIsCreateFormOpen(false)
    if (value) createdCouponId.current = value
  }

  const closeAddForm = () => {
    setIsAddFormOpen(false)
  }

  const closeCreateForm = () => {
    setIsCreateFormOpen(false)
  }

  return (
    <>
      <div className="flex flex-col gap-3 px-4 lg:gap-2 lg:px-0">
        <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center lg:gap-0">
          <div className="bo-color-[rgb(var(--brand-primary-subtle))] flex flex-col gap-3">
            <div className="text-brand-primary flex flex-row items-center gap-1 text-sm font-normal lg:text-[15px]">
              <CouponIcon />
              <span>{t('dashboard.funnel.configuration.offer.coupons')}</span>
            </div>
            <span className="text-sm font-normal">
              {t('dashboard.funnel.configuration.offer.coupons_description')}
            </span>
          </div>
          <AddItemIcon
            onClick={() => {
              setIsAddFormOpen(true)
              setShouldFetchCoupons(true)
            }}
            caption={t('dashboard.funnel.configuration.offer.add_coupon')}
          />
        </div>
        {isAddFormOpen && (
          <AddCouponForm
            coupons={availableCoupons}
            onAddCoupon={handleAddCoupon}
            openCreateForm={openCreateForm}
            closeForm={closeAddForm}
            createdCouponIdRef={createdCouponId}
            couponFieldErrors={couponErrors}
          />
        )}
        {isCreateFormOpen && (
          <CreateCouponForm
            onCreateCoupon={handleCreateCoupon}
            openAddForm={openAddForm}
            closeForm={closeCreateForm}
            createFieldErrors={createFieldErrors}
          />
        )}
        {data?.map(coupon => {
          return (
            <div
              className="border-text-secondary/30 relative flex flex-col rounded-lg border p-2 lg:flex-row lg:items-center"
              key={coupon.id}
            >
              <div className="bo-color-[rgb(var(--brand-primary-subtle))] flex flex-col gap-1 lg:w-1/3">
                <div className="text-brand-primary flex flex-row items-center gap-1 text-sm lg:text-[15px]">
                  <span>{coupon.innerName}</span>
                </div>
                <span className="text-sm lg:text-[15px]">
                  {t('dashboard.coupon.coupon_code')} : {coupon.code}
                </span>
              </div>
              <div className="flex flex-col text-sm lg:w-2/3 lg:flex-row lg:gap-3 lg:text-[15px]">
                <span>
                  {t('dashboard.funnel.configuration.common.coupon.expiration_date')} :{' '}
                  {coupon.expirationDate === null
                    ? t('dashboard.coupon.expiration_date_never')
                    : dateTimeFormat(coupon.expirationDate, {
                        timeFormat: user?.timeFormat,
                        dateFormat: user?.dateFormat,
                        timeZone: user?.timezone,
                      })}
                </span>
                <span>
                  {t('dashboard.funnel.configuration.common.coupon.limit_of_use_title')} :{' '}
                  {coupon.limitOfUse === null
                    ? t('global.unlimited')
                    : t('dashboard.funnel.configuration.common.coupon.limit_of_use', {
                        count_used: coupon.numberOfUse,
                        count_available: coupon.limitOfUse,
                      })}
                </span>
              </div>
              <TrashIcon
                className="absolute right-2 top-2 ml-auto h-[18px] w-[17px] cursor-pointer"
                onClick={() => setCouponToDelete(coupon)}
              />
            </div>
          )
        })}
      </div>
      <Confirmation
        opened={!!couponToDelete}
        onClose={() => setCouponToDelete(null)}
        onConfirm={handleRemoveCoupon}
        confirmationText={t('dashboard.funnel.configuration.common.coupon.remove_confirmation')}
      />
    </>
  )
}

export default CouponSelector
